"use strict";

const gtmHelpers = require("./helpers/gtmHelpers");

const EINSTEIN_TIMEOUT_MS = 5000;

/**
 * Validates and Return the cquotient namespace provided by the commerce cloud platform
 * @returns {Object} - einsteinUtils or null
 */
function getEinsteinUtils() {
    const einsteinUtils = window.CQuotient;
    if (einsteinUtils && (typeof einsteinUtils.getCQUserId === "function") && (typeof einsteinUtils.getCQCookieId === "function")) {
        return einsteinUtils;
    }
    return null;
}

/**
 * Observes the visibility of initialized carousels and triggers the Google Tag Manager (GTM) view item list event
 */
function gtmTrackingObserver() {
    const einsteinCarousels = document.querySelectorAll(".product-tile-carousel-wrapper .einstein-carousel");
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                gtmHelpers.viewItemListEvent(undefined, entry.target);
                gtmHelpers.promotionEvents("view_promotion", entry.target.parentElement);
                observer.unobserve(entry.target);
            }
        });
    });

    einsteinCarousels.forEach((carousel) => {
        observer.observe(carousel);
    });
}

/**
 * Converts the carousel's fallback product IDs into recommendation objects
 * @param {HTMLElement} $parentElement carousel element
 * @returns {Array} fallback recommendations, empty when none are configured
 */
function getFallbackProducts($parentElement) {
    const fallbackProductIds = $parentElement.dataset.fallbackproductids;
    return fallbackProductIds ? fallbackProductIds.split(",").map((id) => ({ id })) : [];
}

/**
 * Renders product tiles into the carousel
 * @param {Array} recommendedProducts products to render, each with an id
 * @param {HTMLElement} $parentElement carousel element
 * @returns {Promise<number>} the number of tiles rendered
 */
async function renderTiles(recommendedProducts, $parentElement) {
    const recommender = $parentElement.dataset.recommender;
    const template = $parentElement.dataset.template;
    const swatches = $parentElement.dataset.swatches;
    const displayRatings = $parentElement.dataset.displayratings;
    const components = recommendedProducts.map(function (recommendedProduct) {
        const tiledefinition = {};
        tiledefinition.template = template;
        tiledefinition.swatches = swatches;
        tiledefinition.displayratings = displayRatings;
        tiledefinition.model = {
            type: "product",
            id: recommendedProduct.id
        };
        return tiledefinition;
    });

    const url = new URL($parentElement.dataset.productLoadUrl);
    url.searchParams.append("components", JSON.stringify(components));
    url.searchParams.append("limit", $parentElement.dataset.limit);
    url.searchParams.append("recommender", recommender);

    // use the product data sent from einstein to get product tile HTML from our controller
    let tileDataHtml;
    try {
        const tileData = await fetch(url.href);
        if (tileData.status !== 200) {
            return 0;
        }
        tileDataHtml = await tileData.text();
    } catch (e) {
        // a network failure counts as nothing rendered, so the caller can still try the fallback
        return 0;
    }
    // the HTML contains product tile divs and separate script tags generated by the isObject tag
    // here we separate the divs to send to the slider and put the script tags below the slider

    const { divs, scripts, amountProductsRendered } = separateDivsAndScripts(tileDataHtml);
    if (amountProductsRendered > 0) {
        // reveal before slickAdd so slick measures a visible track
        $($parentElement).parent(".product-tile-carousel-wrapper").removeClass("d-none");
    }
    scripts.forEach(script => {
        // add after $parentElement
        $($parentElement).parent().append(script);
    });
    divs.forEach(div => {
        if ($($parentElement).hasClass("slick-slider")) {
            $($parentElement).slick("slickAdd", div);
        }
    });

    gtmTrackingObserver();
    return amountProductsRendered;
}

/**
 * fills in the carousel with product tile html objects
 * @param {Object} einsteinResponse cquotient object, or null to use the fallback products
 * @param {HTMLElement} $parentElement parent element where recommendations will show.
 */
async function fillDomElement(einsteinResponse, $parentElement) {
    const recommender = $parentElement.dataset.recommender;
    const recommendedProducts = einsteinResponse && einsteinResponse[recommender] && einsteinResponse[recommender].recs;
    const fallbackProducts = getFallbackProducts($parentElement);

    if (recommendedProducts && recommendedProducts.length > 0) {
        const rendered = await renderTiles(recommendedProducts, $parentElement);
        // Einstein can recommend products that are offline or out of stock here; try the fallback instead
        if (rendered === 0 && fallbackProducts.length > 0) {
            await renderTiles(fallbackProducts, $parentElement);
        }
    } else if (fallbackProducts.length > 0) {
        await renderTiles(fallbackProducts, $parentElement);
    }
}

/**
* Separates div and script elements from an HTML string
* @param {string} htmlString - the HTML string to be parsed
* @returns {Object} - an object containing arrays of divs and scripts
*/
function separateDivsAndScripts(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    // Select all div elements
    const divElements = doc.querySelectorAll("div");

    // Filter out the outer wrapping divs
    const outerDivs = Array.from(divElements).filter((div) => {
        // Check if the div has a parent div
        return div.parentElement && div.parentElement.tagName === "BODY";
    });
    const amountProductsRendered = outerDivs && outerDivs.length;

    // Convert the outerDivs NodeList to an array of HTML strings
    const outerDivsHTML = Array.from(outerDivs).map((div) => div.outerHTML);

    // Select all script elements
    const scriptElements = doc.querySelectorAll("script");

    // Convert the scriptElements NodeList to an array of HTML strings
    const scripts = Array.from(scriptElements).map((script) => script.outerHTML);

    return { divs: outerDivsHTML, scripts, amountProductsRendered };
}

/**
* Populates the Einstein carousel with recommendations
* @param {HTMLElement} carouselEl - the element to populate
* @param {Object} einsteinUtils - the EinsteinUtils object
* @returns {Promise} - a promise that resolves when the carousel is populated
*/
const populateEinsteinCarousel = async (carouselEl, einsteinUtils) => {
    let filled = false;
    /**
    * Fills the carousel once, from whichever of Einstein or the fallback timer comes first
    * @param {Object} einsteinResponse cquotient object, or null for the fallback products
    */
    function fillOnce(einsteinResponse) {
        if (filled) {
            return;
        }
        filled = true;
        fillDomElement(einsteinResponse, carouselEl);
    }

    if (!einsteinUtils) {
        fillOnce(null);
        return;
    }

    // Einstein's script can be blocked (e.g. Safari tracking protection) and then never calls back;
    // without fallback IDs there is nothing to show, so keep waiting for a late answer
    setTimeout(() => {
        if (getFallbackProducts(carouselEl).length > 0) {
            fillOnce(null);
        }
    }, EINSTEIN_TIMEOUT_MS);

    const recommender = carouselEl.dataset.recommender;
    const recommenderType = carouselEl.dataset.recommendertype;
    const params = {
        userId: einsteinUtils.getCQUserId(),
        cookieId: einsteinUtils.getCQCookieId(),
        ccver: "1.01"
    };
    if (recommenderType === "product" || recommenderType === "category") {
        params.anchors = [{id : carouselEl.dataset.context }];
    }
    /**
    * Processes a recommendation responses
    * @param {Object} einsteinResponse cquotient object
    */
    function recommendationsReceived(einsteinResponse) {
        fillOnce(einsteinResponse);
    }

    if (einsteinUtils.getRecs) {
        einsteinUtils.getRecs(einsteinUtils.clientId, recommender, params, recommendationsReceived);
    } else {
        einsteinUtils.widgets = einsteinUtils.widgets || []; // eslint-disable-line no-param-reassign
        einsteinUtils.widgets.push({
            recommenderName: recommender,
            parameters: params,
            callback: recommendationsReceived
        });
    }
};

/**
* Loads product recommendations using Einstein API and populates the corresponding carousels on the page
* @async
* @returns {Promise<void>}
*/
const loadRecommendations = async () => {
    const einsteinUtils = getEinsteinUtils();
    document.querySelectorAll(".einstein-carousel").forEach((carousel) => {
        populateEinsteinCarousel(carousel, einsteinUtils);
    });
};

$(document).ready(function () {
    loadRecommendations();
});
